import { useLayoutEffect, useState, type RefCallback } from 'react'
import { isDefined, isNotDefined } from '@terros/common'
import { type MantineBreakpoint, px, useMantineTheme } from '@mantine/core'

export type ContainerBreakpoint = {
  ref: RefCallback<HTMLDivElement>
  isAtLeast: boolean
}

export const useContainerBreakpoint = (breakpoint: MantineBreakpoint): ContainerBreakpoint => {
  const { breakpoints } = useMantineTheme()
  const minWidth = toPixels(breakpoints[breakpoint])
  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const [isAtLeast, setIsAtLeast] = useState(true)

  useLayoutEffect(() => {
    const boundary = isDefined(element) ? findScrollBoundary(element) : undefined
    if (isNotDefined(boundary)) {
      setIsAtLeast(true)
      return
    }
    setIsAtLeast(isContainerAtLeast(measureStableWidth(boundary), minWidth))

    const observer = new ResizeObserver(() => {
      setIsAtLeast(isContainerAtLeast(measureStableWidth(boundary), minWidth))
    })
    observer.observe(boundary)
    return () => observer.disconnect()
  }, [element, minWidth])

  return { ref: setElement, isAtLeast }
}

export const isContainerAtLeast = (width: number | undefined, minWidth: number): boolean => {
  if (isNotDefined(width)) return true
  if (Number.isNaN(minWidth)) return true
  return width >= minWidth
}

export const findScrollBoundary = (element: HTMLElement): HTMLElement | undefined => {
  let current = element.parentElement
  while (isDefined(current)) {
    if (/auto|scroll|hidden/.test(getComputedStyle(current).overflow)) return current
    current = current.parentElement
  }
  return element.parentElement ?? undefined
}

export const measureStableWidth = (element: HTMLElement): number => {
  const style = getComputedStyle(element)
  const horizontalInset =
    parseFloat(style.paddingLeft) +
    parseFloat(style.paddingRight) +
    parseFloat(style.borderLeftWidth) +
    parseFloat(style.borderRightWidth)
  return element.getBoundingClientRect().width - horizontalInset
}

const toPixels = (value: string): number => {
  const converted = px(value)
  return typeof converted === 'number' ? converted : Number.NaN
}
